import { useState } from "react";
import { CalendarClock, CalendarPlus, Download, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const localInput = (iso) => {
  const d = iso ? new Date(iso) : new Date(Date.now() + 864e5);
  return new Date(d.getTime() - d.getTimezoneOffset() * 6e4).toISOString().slice(0, 16);
};

export const formatLocal = (iso) => new Date(iso).toLocaleString(undefined, {
  weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZoneName: "short",
});

export const ScheduleCall = ({ expId, scheduled, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState(localInput(scheduled?.at));
  const [note, setNote] = useState(scheduled?.note || "");
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try { const { data } = await fn(); onSaved(data.scheduled); setOpen(false); toast.success(data.scheduled ? "Call scheduled. Everyone on the trip has been emailed." : "Scheduled call cleared"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const save = () => run(() => api.put(`/calls/experience/${expId}/schedule`, { at: new Date(at).toISOString(), note }));
  const clear = () => run(() => api.delete(`/calls/experience/${expId}/schedule`));

  if (!open) return (
    <div className="flex gap-2">
      <button type="button" className="btn-outline h-11 px-4 text-sm" onClick={() => setOpen(true)} data-testid="group-call-schedule-button"><CalendarClock className="h-4 w-4" /> {scheduled ? "Change time" : "Schedule"}</button>
      {scheduled && <button type="button" className="btn-outline h-11 w-11 px-0" aria-label="Clear scheduled call" disabled={busy} onClick={clear} data-testid="group-call-clear-button"><X className="h-4 w-4" /></button>}
    </div>
  );
  return (
    <div className="w-full space-y-3 border-t border-border pt-4" data-testid="group-call-schedule-form">
      <div className="grid gap-3 sm:grid-cols-[220px_1fr]">
        <div><label htmlFor="gc-at" className="field-label">Time (your time zone)</label><input id="gc-at" type="datetime-local" min={localInput(new Date(Date.now() + 6e5).toISOString())} className="field" value={at} onChange={(e) => setAt(e.target.value)} data-testid="group-call-time-input" /></div>
        <div><label htmlFor="gc-note" className="field-label">Note (optional)</label><input id="gc-note" className="field" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Finalise the villa and flights" data-testid="group-call-note-input" /></div>
      </div>
      <div className="flex gap-2">
        <button type="button" className="btn-primary h-11 px-5 text-sm" disabled={busy || !at} onClick={save} data-testid="group-call-schedule-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save and email everyone"}</button>
        <button type="button" className="btn-outline h-11 px-4 text-sm" onClick={() => setOpen(false)} data-testid="group-call-schedule-cancel">Cancel</button>
      </div>
    </div>
  );
};

export const CalendarButtons = ({ scheduled }) => (
  <div className="mt-2 flex flex-wrap gap-2" data-testid="group-call-calendar">
    <a href={scheduled.google_url} target="_blank" rel="noopener noreferrer" className="btn-outline h-9 px-3 text-xs" data-testid="group-call-google-calendar"><CalendarPlus className="h-3.5 w-3.5" /> Google Calendar</a>
    <a href={`${process.env.REACT_APP_BACKEND_URL}${scheduled.ics_path}`} download="group-call.ics" className="btn-outline h-9 px-3 text-xs" data-testid="group-call-ics-download"><Download className="h-3.5 w-3.5" /> Apple / Outlook (.ics)</a>
  </div>
);
